import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

const AuthContext = createContext(null);

const allowedAdminRoles = ["super_admin", "admin", "staff"];

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem("accessToken"));
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem("refreshToken"));
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) return null;

    try {
      return JSON.parse(storedUser);
    } catch {
      localStorage.removeItem("user");
      return null;
    }
  });

  const [authChecking, setAuthChecking] = useState(true);

  const isAuthenticated = Boolean(accessToken && user);

  const loadPermissions = async (token) => {
    try {
      const response = await fetch("/api/access-control/my-permissions/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return [];
      }

      return data.permissions || [];
    } catch {
      return [];
    }
  };

  const login = async ({ access, refresh, user: userData }) => {
    if (!access || !userData) {
      return null;
    }

    localStorage.setItem("accessToken", access);
    setAccessToken(access);

    if (refresh) {
      localStorage.setItem("refreshToken", refresh);
      setRefreshToken(refresh);
    }

    const baseUser = {
      ...userData,
      permissions: userData.permissions || [],
    };

    localStorage.setItem("user", JSON.stringify(baseUser));
    setUser(baseUser);

    const permissions = await loadPermissions(access);

    const finalUser = {
      ...baseUser,
      permissions,
    };

    localStorage.setItem("user", JSON.stringify(finalUser));
    setUser(finalUser);

    return finalUser;
  };

  const logout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");

    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  const validateSession = async () => {
    const token = localStorage.getItem("accessToken");
    const refresh = localStorage.getItem("refreshToken");

    if (!token) {
      setAuthChecking(false);
      return null;
    }

    try {
      const response = await fetch("/api/auth/me/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        logout();
        setAuthChecking(false);
        return null;
      }

      const finalUser = await login({
        access: token,
        refresh,
        user: data,
      });

      setAuthChecking(false);
      return finalUser;
    } catch {
      logout();
      setAuthChecking(false);
      return null;
    }
  };

  const refreshUserPermissions = async () => {
    const token = localStorage.getItem("accessToken");

    if (!token || !user) {
      return [];
    }

    const permissions = await loadPermissions(token);

    const updatedUser = {
      ...user,
      permissions,
    };

    localStorage.setItem("user", JSON.stringify(updatedUser));
    setUser(updatedUser);

    return permissions;
  };

  useEffect(() => {
    validateSession();
  }, []);

  const value = useMemo(
    () => ({
      accessToken,
      refreshToken,
      user,
      isAuthenticated,
      authChecking,
      login,
      logout,
      validateSession,
      refreshUserPermissions,
    }),
    [accessToken, refreshToken, user, isAuthenticated, authChecking]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, authChecking, user } = useAuth();
  const location = useLocation();

  if (authChecking) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/iniciar-sesion" replace state={{ from: location }} />;
  }

  const validRoles = allowedRoles || allowedAdminRoles;

  if (!user?.role || !validRoles.includes(user.role)) {
    return <Navigate to="/iniciar-sesion" replace />;
  }

  return children;
};